import React from "react";
import { cn } from "../../lib/utils";

/**
 * Typography components based on Shadcn UI style
 * Replaces Material UI Typography with a Tailwind CSS implementation
 */

export function H1({ className, children = null, ...props }) {
  return (
    <h1
      className={cn(
        "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
        className
      )}
      {...props}
    >
      {children}
    </h1>
  );
}

export function H2({ className, children = null, ...props }) {
  return (
    <h2
      className={cn(
        "scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0",
        className
      )}
      {...props}
    >
      {children}
    </h2>
  );
}

export function H3({ className, children = null, ...props }) {
  return (
    <h3
      className={cn(
        "scroll-m-20 text-2xl font-semibold tracking-tight",
        className
      )}
      {...props}
    >
      {children}
    </h3>
  );
}

export function H4({ className, children = null, ...props }) {
  return (
    <h4
      className={cn(
        "scroll-m-20 text-xl font-semibold tracking-tight",
        className
      )}
      {...props}
    >
      {children}
    </h4>
  );
}

export function P({ className, ...props }) {
  return (
    <p
      className={cn("leading-7", className)}
      {...props}
    />
  );
}

export function Blockquote({ className, ...props }) {
  return (
    <blockquote
      className={cn(
        "mt-6 border-l-2 pl-6 italic text-muted-foreground",
        className
      )}
      {...props}
    />
  );
}

export function InlineCode({ className, ...props }) {
  return (
    <code
      className={cn(
        "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm",
        className
      )}
      {...props}
    />
  );
}

export function Lead({ className, ...props }) {
  return (
    <p
      className={cn("text-xl text-muted-foreground", className)}
      {...props}
    />
  );
}

export function Large({ className, ...props }) {
  return (
    <div
      className={cn("text-lg font-semibold", className)}
      {...props}
    />
  );
}

export function Small({ className, ...props }) {
  return (
    <small
      className={cn("text-sm font-medium leading-none", className)}
      {...props}
    />
  );
}

export function Muted({ className, ...props }) {
  return (
    <p
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export function Typography({ variant = "body1", className, ...props }) {
  const variantMap = {
    h1: H1,
    h2: H2,
    h3: H3,
    h4: H4,
    body1: P,
    body2: Small,
    subtitle1: Lead,
    subtitle2: Muted,
    caption: Small,
    button: Large,
  };

  const Component = variantMap[variant] || P;

  return <Component className={className} {...props} />;
}
